/**
 * Live Tables Dashboard
 * Admin view of all active table sessions with:
 * - Real-time order items display
 * - Ability to add items to any table
 * - Waiter request management
 * - Bill creation and payment handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  UtensilsCrossed, Users, Clock, Bell, Plus, Check, X, 
  Receipt, CreditCard, ChefHat, AlertCircle, RefreshCw,
  Eye, Trash2, Edit, Send, MessageSquare, Coffee, HandMetal,
  ArrowRight, PlusCircle, Search, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Status colors and labels
const STATUS_CONFIG = {
  seated: { color: 'bg-blue-100 text-blue-700', label: 'Seated' },
  ordering: { color: 'bg-amber-100 text-amber-700', label: 'Ordering' },
  pending: { color: 'bg-orange-100 text-orange-700', label: 'Pending' },
  preparing: { color: 'bg-purple-100 text-purple-700', label: 'Preparing' },
  served: { color: 'bg-green-100 text-green-700', label: 'Served' },
  awaiting_payment: { color: 'bg-indigo-100 text-indigo-700', label: 'Bill Ready' },
  completed: { color: 'bg-slate-100 text-slate-700', label: 'Completed' },
};

const ITEM_STATUS_CONFIG = {
  awaiting_confirmation: { color: 'bg-yellow-100 text-yellow-700', label: 'Awaiting' },
  pending: { color: 'bg-orange-100 text-orange-700', label: 'Pending' },
  preparing: { color: 'bg-purple-100 text-purple-700', label: 'Preparing' },
  ready: { color: 'bg-blue-100 text-blue-700', label: 'Ready' },
  served: { color: 'bg-green-100 text-green-700', label: 'Served' },
};

export default function LiveTables() {
  const { api, token, user } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  
  // Data state
  const [sessions, setSessions] = useState([]);
  const [tables, setTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog state
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showSessionDetails, setShowSessionDetails] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedMenuCategory, setSelectedMenuCategory] = useState('all');
  const [addingItemQuantity, setAddingItemQuantity] = useState(1);
  const [addingItemNotes, setAddingItemNotes] = useState('');
  
  // WebSocket for real-time updates
  const wsRef = useRef(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!selectedRestaurant?.id) return;
    
    try {
      setLoading(true);
      
      const [sessionsRes, tablesRes, menuRes] = await Promise.all([
        api.get(`/restaurants/${selectedRestaurant.id}/table-sessions`),
        api.get(`/restaurants/${selectedRestaurant.id}/tables`),
        api.get(`/restaurants/${selectedRestaurant.id}/menu`)
      ]);
      
      setSessions(sessionsRes.data || []);
      setTables(tablesRes.data || []);
      setMenuItems(menuRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load table sessions');
    } finally {
      setLoading(false);
    }
  }, [api, selectedRestaurant?.id]);

  useEffect(() => {
    fetchData();
    // Refresh every 15 seconds
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!token || !selectedRestaurant?.id) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl) return;

    try {
      wsRef.current = new WebSocket(`${wsUrl}/ws/notifications?token=${token}`);
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification' && 
              (data.data.type === 'table_order' || data.data.type === 'waiter_request')) {
            // Refresh sessions when relevant notification received
            fetchData();
          }
        } catch (e) {
          // Ignore parse errors
        }
      };
    } catch (error) {
      console.error('WebSocket error:', error);
    }

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [token, selectedRestaurant?.id, fetchData]);

  // Filter sessions
  const getFilteredSessions = () => {
    let filtered = sessions;
    
    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(s => !['completed', 'cancelled'].includes(s.status));
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.table_number?.toString().includes(query) ||
        s.customer_name?.toLowerCase().includes(query)
      );
    }
    
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // Get menu categories
  const getCategories = () => {
    const cats = new Set(menuItems.map(item => item.category).filter(Boolean));
    return ['all', ...Array.from(cats)];
  };

  // Get filtered menu for add item dialog
  const getFilteredMenu = () => {
    if (selectedMenuCategory === 'all') return menuItems;
    return menuItems.filter(item => item.category === selectedMenuCategory);
  };

  // Add item to session (admin)
  const addItemToSession = async (menuItem) => {
    if (!selectedSession) return;
    
    try {
      await api.post(`/table-sessions/${selectedSession.id}/items`, {
        menu_item_id: menuItem.id,
        quantity: addingItemQuantity,
        notes: addingItemNotes,
        added_by: 'admin',
        requires_confirmation: false
      });
      
      toast.success(`Added ${addingItemQuantity}x ${menuItem.name} to Table ${selectedSession.table_number}`);
      setShowAddItemDialog(false);
      setAddingItemQuantity(1);
      setAddingItemNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  // Update item status
  const updateItemStatus = async (sessionId, itemId, status) => {
    try {
      await api.put(`/table-sessions/${sessionId}/items/${itemId}`, { status });
      toast.success(`Item marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  // Remove item from session
  const removeItem = async (sessionId, itemId) => {
    if (!window.confirm('Remove this item from the order?')) return;
    
    try {
      await api.delete(`/table-sessions/${sessionId}/items/${itemId}?reason=Removed by admin`);
      toast.success('Item removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  // Acknowledge waiter request
  const acknowledgeRequest = async (sessionId, requestId) => {
    try {
      await api.put(`/table-sessions/${sessionId}/waiter-requests/${requestId}`, { 
        status: 'acknowledged' 
      });
      toast.success('Request acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge request');
    }
  };

  // Complete waiter request
  const completeRequest = async (sessionId, requestId) => {
    try {
      await api.put(`/table-sessions/${sessionId}/waiter-requests/${requestId}`, { 
        status: 'completed' 
      });
      toast.success('Request completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete request');
    }
  };

  // Update session status
  const updateSessionStatus = async (sessionId, status) => {
    try {
      await api.put(`/table-sessions/${sessionId}`, { status });
      toast.success(`Session marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  // Generate bill / request payment
  const requestBill = async (sessionId) => {
    try {
      await api.post(`/table-sessions/${sessionId}/request-bill`);
      toast.success('Bill generated');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate bill');
    }
  };

  // Complete session
  const completeSession = async (sessionId) => {
    try {
      await api.put(`/table-sessions/${sessionId}`, { 
        status: 'completed',
        completed_at: new Date().toISOString()
      });
      toast.success('Session completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete session');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    const currency = selectedRestaurant?.currency || 'GBP';
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency 
    }).format(amount || 0);
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Get session duration
  const getDuration = (startTime) => {
    if (!startTime) return '';
    const mins = Math.floor((Date.now() - new Date(startTime).getTime()) / 60000);
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  // Count pending requests
  const getPendingRequestsCount = (session) => {
    return (session.waiter_requests || []).filter(r => r.status === 'pending').length;
  };

  // Count pending items
  const getPendingItemsCount = (session) => {
    return (session.items || []).filter(i => 
      ['pending', 'awaiting_confirmation'].includes(i.status)
    ).length;
  };

  const filteredSessions = getFilteredSessions();
  const activeSessionsCount = sessions.filter(s => !['completed', 'cancelled'].includes(s.status)).length;
  const pendingRequestsTotal = sessions.reduce((acc, s) => acc + getPendingRequestsCount(s), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UtensilsCrossed className="w-6 h-6 text-orange-500" />
            Live Tables
          </h1>
          <p className="text-slate-500 mt-1">
            {activeSessionsCount} active table{activeSessionsCount !== 1 ? 's' : ''} • {pendingRequestsTotal} pending request{pendingRequestsTotal !== 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={fetchData}
            data-testid="refresh-tables-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Active Tables</p>
                <p className="text-2xl font-bold text-blue-700">{activeSessionsCount}</p>
              </div>
              <Users className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Pending Orders</p>
                <p className="text-2xl font-bold text-orange-700">
                  {sessions.reduce((acc, s) => acc + getPendingItemsCount(s), 0)}
                </p>
              </div>
              <Clock className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={`${pendingRequestsTotal > 0 ? 'bg-red-50 border-red-200 animate-pulse' : 'bg-amber-50 border-amber-200'}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${pendingRequestsTotal > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                  Waiter Requests
                </p>
                <p className={`text-2xl font-bold ${pendingRequestsTotal > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                  {pendingRequestsTotal}
                </p>
              </div>
              <Bell className={`w-8 h-8 ${pendingRequestsTotal > 0 ? 'text-red-400' : 'text-amber-400'}`} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-700">
                  {formatCurrency(sessions.reduce((acc, s) => acc + (s.subtotal || 0), 0))}
                </p>
              </div>
              <Receipt className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search table or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-tables"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active Only</SelectItem>
            <SelectItem value="all">All Sessions</SelectItem>
            <SelectItem value="seated">Seated</SelectItem>
            <SelectItem value="ordering">Ordering</SelectItem>
            <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Sessions Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-6 bg-slate-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-32 bg-slate-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredSessions.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <UtensilsCrossed className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Active Tables</h3>
            <p className="text-slate-500">Table sessions will appear here when customers are seated</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSessions.map(session => {
            const pendingRequests = getPendingRequestsCount(session);
            const pendingItems = getPendingItemsCount(session);
            const statusConfig = STATUS_CONFIG[session.status] || STATUS_CONFIG.seated;
            
            return (
              <Card 
                key={session.id} 
                className={`overflow-hidden transition-all hover:shadow-lg ${
                  pendingRequests > 0 ? 'ring-2 ring-red-400 animate-pulse' : ''
                }`}
                data-testid={`table-session-${session.id}`}
              >
                <CardHeader className="pb-2 bg-gradient-to-r from-slate-50 to-white border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <span className="text-xl font-bold text-orange-600">
                          {session.table_number}
                        </span>
                      </div>
                      <div>
                        <CardTitle className="text-base">Table {session.table_number}</CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          <Users className="w-3 h-3" />
                          {session.party_size || 2} guests
                          <span className="text-slate-300">•</span>
                          <Clock className="w-3 h-3" />
                          {getDuration(session.started_at || session.created_at)}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge className={statusConfig.color}>
                      {statusConfig.label}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-4 space-y-4">
                  {/* Customer Name */}
                  {session.customer_name && (
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Customer:</span> {session.customer_name}
                    </p>
                  )}

                  {/* Waiter Requests Alert */}
                  {pendingRequests > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Bell className="w-5 h-5 text-red-500 animate-bounce" />
                        <span className="font-semibold text-red-700">
                          {pendingRequests} Waiter Request{pendingRequests > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {(session.waiter_requests || [])
                          .filter(r => r.status === 'pending')
                          .map(request => (
                            <div key={request.id} className="flex items-center justify-between text-sm">
                              <span className="text-red-600">{request.type}</span>
                              <div className="flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => acknowledgeRequest(session.id, request.id)}
                                  className="h-7 text-xs"
                                >
                                  Acknowledge
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={() => completeRequest(session.id, request.id)}
                                  className="h-7 text-xs btn-primary"
                                >
                                  <Check className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">
                        Order Items ({(session.items || []).length})
                      </span>
                      {pendingItems > 0 && (
                        <Badge className="bg-orange-100 text-orange-700">
                          {pendingItems} pending
                        </Badge>
                      )}
                    </div>
                    
                    {(session.items || []).length === 0 ? (
                      <p className="text-sm text-slate-400 italic">No items ordered yet</p>
                    ) : (
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {(session.items || []).slice(0, 5).map(item => {
                          const itemStatus = ITEM_STATUS_CONFIG[item.status] || ITEM_STATUS_CONFIG.pending;
                          return (
                            <div 
                              key={item.id} 
                              className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0"
                            >
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{item.quantity}x</span>
                                <span className="text-slate-700">{item.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={`${itemStatus.color} text-xs py-0`}>
                                  {itemStatus.label}
                                </Badge>
                                <span className="text-slate-500">
                                  {formatCurrency(item.total)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        {(session.items || []).length > 5 && (
                          <p className="text-xs text-slate-400 text-center pt-1">
                            +{(session.items || []).length - 5} more items
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Total */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="font-semibold text-slate-700">Total</span>
                    <span className="text-lg font-bold text-orange-600">
                      {formatCurrency(session.subtotal || 0)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedSession(session);
                        setShowAddItemDialog(true);
                      }}
                      data-testid={`add-item-${session.id}`}
                    >
                      <PlusCircle className="w-4 h-4 mr-1" />
                      Add Item
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedSession(session);
                        setShowSessionDetails(true);
                      }}
                      data-testid={`view-details-${session.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Details
                    </Button>
                    
                    {session.status !== 'awaiting_payment' && session.status !== 'completed' && (
                      <Button
                        size="sm"
                        onClick={() => requestBill(session.id)}
                        className="btn-primary"
                        data-testid={`request-bill-${session.id}`}
                      >
                        <Receipt className="w-4 h-4 mr-1" />
                        Bill
                      </Button>
                    )}
                    
                    {session.status === 'awaiting_payment' && (
                      <Button
                        size="sm"
                        onClick={() => completeSession(session.id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`complete-${session.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Complete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Add Item to Table {selectedSession?.table_number}
            </DialogTitle>
            <DialogDescription>
              Select menu items to add to this table's order
            </DialogDescription>
          </DialogHeader>
          
          {/* Category filter */}
          <div className="flex gap-2 overflow-x-auto py-2">
            {getCategories().map(cat => (
              <Button
                key={cat}
                size="sm"
                variant={selectedMenuCategory === cat ? 'default' : 'outline'}
                onClick={() => setSelectedMenuCategory(cat)}
                className={selectedMenuCategory === cat ? 'btn-primary' : ''}
              >
                {cat === 'all' ? 'All' : cat}
              </Button>
            ))}
          </div>
          
          {/* Menu items */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              {getFilteredMenu().map(item => (
                <Card 
                  key={item.id} 
                  className="cursor-pointer hover:border-orange-300 transition-colors"
                  onClick={() => addItemToSession(item)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{item.name}</p>
                        <p className="text-sm text-slate-500">{item.category}</p>
                      </div>
                      <p className="font-bold text-orange-600">
                        {formatCurrency(item.price)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Details Dialog */}
      <Dialog open={showSessionDetails} onOpenChange={setShowSessionDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Table {selectedSession?.table_number} Details
              {selectedSession && (
                <Badge className={STATUS_CONFIG[selectedSession.status]?.color}>
                  {STATUS_CONFIG[selectedSession.status]?.label}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedSession?.customer_name && `Customer: ${selectedSession.customer_name}`}
              {selectedSession?.party_size && ` • ${selectedSession.party_size} guests`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* All Items with actions */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Order Items</h4>
              {(selectedSession?.items || []).length === 0 ? (
                <p className="text-slate-400 italic">No items</p>
              ) : (
                <div className="space-y-2">
                  {(selectedSession?.items || []).map(item => {
                    const itemStatus = ITEM_STATUS_CONFIG[item.status] || ITEM_STATUS_CONFIG.pending;
                    return (
                      <div 
                        key={item.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{item.quantity}x {item.name}</span>
                            <Badge className={`${itemStatus.color} text-xs`}>
                              {itemStatus.label}
                            </Badge>
                          </div>
                          {item.notes && (
                            <p className="text-xs text-slate-500 mt-1">Note: {item.notes}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            Added by {item.added_by_name || item.added_by} at {formatTime(item.added_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-700">
                            {formatCurrency(item.total)}
                          </span>
                          {/* Status actions */}
                          {item.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateItemStatus(selectedSession.id, item.id, 'preparing')}
                            >
                              <ChefHat className="w-4 h-4" />
                            </Button>
                          )}
                          {item.status === 'preparing' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateItemStatus(selectedSession.id, item.id, 'ready')}
                            >
                              Ready
                            </Button>
                          )}
                          {item.status === 'ready' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateItemStatus(selectedSession.id, item.id, 'served')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeItem(selectedSession.id, item.id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Waiter Requests */}
            {(selectedSession?.waiter_requests || []).length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Waiter Requests</h4>
                <div className="space-y-2">
                  {(selectedSession?.waiter_requests || []).map(request => (
                    <div 
                      key={request.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        request.status === 'pending' ? 'bg-red-50' : 'bg-slate-50'
                      }`}
                    >
                      <div>
                        <p className={`font-medium ${request.status === 'pending' ? 'text-red-700' : 'text-slate-700'}`}>
                          {request.type}
                        </p>
                        {request.message && (
                          <p className="text-sm text-slate-500">{request.message}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          {formatTime(request.requested_at)}
                        </p>
                      </div>
                      {request.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeRequest(selectedSession.id, request.id)}
                          >
                            Acknowledge
                          </Button>
                          <Button
                            size="sm"
                            className="btn-primary"
                            onClick={() => completeRequest(selectedSession.id, request.id)}
                          >
                            Complete
                          </Button>
                        </div>
                      )}
                      {request.status !== 'pending' && (
                        <Badge className="bg-green-100 text-green-700">
                          {request.status}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total */}
            <div className="pt-4 border-t">
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-orange-600">
                  {formatCurrency(selectedSession?.subtotal || 0)}
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSessionDetails(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setShowSessionDetails(false);
                setShowAddItemDialog(true);
              }}
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Item
            </Button>
            {selectedSession?.status !== 'completed' && (
              <Button
                onClick={() => {
                  requestBill(selectedSession?.id);
                  setShowSessionDetails(false);
                }}
                className="btn-primary"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Generate Bill
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
